<script setup>
    import { ref, computed, onMounted } from 'vue'
    import { useRoute, useRouter, RouterLink } from 'vue-router'
    import SNBAdmin from '../../../../layouts/nav/SNBAdmin.vue'
    import fileService from '../../../../services/fileService';
    import certificateTemplateService from '../../services/lecture/certificateTemplateService';
    import { usePageLoadingStore } from '../../../../stores/pageLoading';
    import { useAlertStore } from '../../../../stores/alertStore';

    const { $alert } = useAlertStore();
    const route = useRoute()
    const router = useRouter()
    const loadingStore = usePageLoadingStore()

    const input = ref({
        templateId: '',
        certificateName: '',
        frontFixedText: '',
        frontImageName: "", // 앞면 이미지명
        frontImageSize: "", // 앞면 이미지사이즈  
        frontImageDir: "", // 앞면 이미지위치
        backImageName: "", // 뒷면 이미지명
        backImageSize: "", // 뒷면 이미지사이즈
        backImageDir: "", // 뒷면 이미지위치
    })

    const inputError = ref({
        certificateName: '',
        frontFixedText: '',
        frontImageName: "", // 앞면 이미지명
        backImageName: "", // 뒷면 이미지명
    })

    onMounted(()=>{
        const { id } = route.params;
        if(!id) {
            router.push({name:'LectureCertificateTemplateManage'});
        } else {
            input.value = {
                ...input.value,
                templateId: id,
            }
            retrieveDetail(id);
        }
    });

    const retrieveDetail = (id) => {
        loadingStore.loading = true;
        certificateTemplateService.retrieveCertificateTemplateDetail(id)
            .then( (res) => {
                loadingStore.loading = false;
                // console.log(res);
                if(res.data.code.toLowerCase() === 'success') {
                    const result = res.data.result;
                    input.value = result;
                }
            }).catch( async (err) =>  {
                loadingStore.loading = false;
                console.log(err);
            });
    };

    const uploadCertificateFile = (type) => {
        const fileInput = document.createElement('input');
        fileInput.type= 'file';
        fileInput.accept = 'image/*';
        fileInput.click();

        fileInput.addEventListener('change', function(event){
            // console.log(event.target.files);
            const file = event.target.files[0];
            const fileSize = event.target.files[0].size;
            if(fileSize >= 1024 * 1024 * 200) {
                $alert('최대 200MB까지 첨부 가능합니다.');
                return;
            }

            let paramObj = {
                container: 'public',
                folder: 'certificateTemplate',
                file,
            }
            fileService.uploadFile(paramObj)
                .then((res) => {
                    // console.log(res);
                    if(res.data.code.toLowerCase() === 'success') {
                        const result = res.data.result;
                        // console.log(result);
                        if(type === 'front') {
                            input.value.frontImageName = result.originalFilename;
                            input.value.frontImageSize = result.fileSize;
                            input.value.frontImageDir = result.blobUrl;
                        } else if(type === 'back') {
                            input.value.backImageName = result.originalFilename;
                            input.value.backImageSize = result.fileSize;
                            input.value.backImageDir = result.blobUrl;
                        }
                    }
                })
                .catch( (error) =>  {
                    console.log(error);
                });
        });
    }

    const removeCertificateFile = (type) => {
        if(type === 'front') {
            input.value.frontImageName = '';
            input.value.frontImageSize = '';
            input.value.frontImageDir = '';
        } else if(type === 'back') {
            input.value.backImageName = '';
            input.value.backImageSize = '';
            input.value.backImageDir = '';
        }
    };

    const update = () => {
        const { templateId, certificateName, frontFixedText, frontImageName, frontImageSize, frontImageDir, backImageName, backImageSize, backImageDir } = input.value;
        let isInvalid = false;

        inputError.value = {
            certificateName: '',
            frontFixedText: '',
            frontImageName: '',
            backImageName: '',  
        };

        if(!certificateName) {
            inputError.value.certificateName = '수료증명을 입력하세요.';
            isInvalid = true;
        } else if(certificateName.length > 20) {
            inputError.value.certificateName = '수료증명은 20자 이내로 입력하세요.';
            isInvalid = true;
        }

        if(!frontFixedText) {
            inputError.value.frontFixedText = '앞면 고정 문구를 입력하세요.';
            isInvalid = true;
        } else if(frontFixedText.length > 60) {
            inputError.value.frontFixedText = '앞면 고정 문구는 60자 이내로 입력하세요.';
            isInvalid = true;
        }

        if(!frontImageName) {
            inputError.value.frontImageName = '앞면 이미지를 업로드하세요.';
            isInvalid = true;
        }
        if(!backImageName) {
            inputError.value.backImageName = '뒷면 이미지를 업로드하세요.';
            isInvalid = true;
        }

        if(!isInvalid) {
            const paramObj = {
                templateId,
                certificateName,
                frontFixedText,
                frontImageName,
                frontImageSize,
                frontImageDir,
                backImageName,
                backImageSize,
                backImageDir,
            };

            loadingStore.loading = true;
            certificateTemplateService.updateTemplate(paramObj)
                .then( async (res) => {
                    loadingStore.loading = false;
                    if(res.data.code.toLowerCase() === 'success') {
                        await $alert("수료증 양식을 수정했습니다.");
                        goBack();
                    } else {
                        $alert("수정을 실패하였습니다.");
                    }
                }).catch( (res) =>  {
                    loadingStore.loading = false;
                    // console.log(res);
                    $alert("수정을 실패하였습니다.");
                });
        }
    }

    const goBack = () => {
        router.push({ name: 'LectureCertificateTemplateDetail', params: { id: input.value.templateId }});
    };
</script>

<template>
    <div class="content">
        <div class="lecture-wrap">
            <div class="sub-title">
                <h3>수료증 양식 수정</h3>
                <p><span>*표시는 필수 입력사항</span>입니다.</p>
            </div>
            <div class="table-form">
                <table class="vertical">
                    <colgroup>
                        <col width="10%" />
                        <col />
                        <col />
                        <col />
                    </colgroup>
                    <tr>
                        <th><span class="mark">수료증명</span></th>
                        <td>
                            <KSAInput v-model="input.certificateName" :msg="inputError.certificateName" placeholder="수료증명을 20자 이내로 입력하세요." max-length="20"/>
                        </td>
                    </tr>
                    <tr>
                        <th><span class="mark">수료증 이미지</span><br><span>(앞면)</span></th>
                        <td class="text-box">
                            <div class="grid file">
                                <KSAPrimaryButton text="파일 선택" @click="uploadCertificateFile('front')" />
                                <KSAInput class="input icon" placeholder="파일 선택(확장자 png, jpg, svg이미지파일, 가로x세로(843x596)px 사이즈를 권장합니다.)" v-model="input.frontImageName" disabled />
                                <button class="delete" :disabled="!input.frontImageName" @click="removeCertificateFile('front')"><i></i></button>
                            </div>
                            <small>최대 200MB 까지 첨부 가능</small>
                            <div>
                                <small style="color:#E71414;">{{ inputError.frontImageName }}</small>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <th><span class="mark">수료증 이미지</span><br><span>(뒷면)</span></th>
                        <td class="text-box">
                            <div class="grid file">
                                <KSAPrimaryButton text="파일 선택" @click="uploadCertificateFile('back')" />
                                <KSAInput class="input icon" placeholder="파일 선택(확장자 png, jpg, svg이미지파일, 가로x세로(843x596)px 사이즈를 권장합니다.)" v-model="input.backImageName" disabled />
                                <button class="delete" :disabled="!input.backImageName" @click="removeCertificateFile('back')"><i></i></button>
                            </div>
                            <small>최대 200MB 까지 첨부 가능</small>
                            <div>
                                <small style="color:#E71414;">{{ inputError.backImageName }}</small>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <th><span class="mark">앞면 고정 문구</span></th>
                        <td>
                            <KSAInput v-model="input.frontFixedText" :msg="inputError.frontFixedText" max-length="60" placeholder="앞면 고정 문구를 60자 이내로 입력하세요."/>
                        </td>
                    </tr>
                </table>
            </div>
            <div class="btn-wrap space-between">
                <KSASecondaryButton class="gray_button" text="취소" @click="goBack" />
                <KSAPrimaryButton text="수정" :disabled="!input.certificateName || !input.frontImageName || !input.backImageName || !input.frontFixedText" @click="update" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import "../../../../assets/scss/theme";

    small {
        @include font-size_xs;
        color: $gray_color;
    }
    
    .file {
        margin-top: 5px;
    }
</style>